import React, { useState, useEffect } from 'react';
import { Box, LinearProgress, Typography, Button, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/authService';

const TrialIndicator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trialInfo, setTrialInfo] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    if (!user?.trial) return;

    setTrialInfo(user.trial);

    // Show alert if trial is running low (less than 15 minutes)
    if (user.trial.trialActive && user.trial.remainingMinutes <= 15) {
      setShowAlert(true);
    }

    // Set up heartbeat if trial is active
    if (user.trial.trialActive && user.trial.remainingMinutes > 0) {
      const heartbeatInterval = setInterval(async () => {
        try {
          const response = await api.post('/auth/trial/heartbeat');
          setTrialInfo(prev => ({
            ...prev,
            remainingMinutes: response.data.remainingMinutes,
            expired: response.data.expired
          }));

          if (response.data.expired) {
            setShowAlert(true);
            clearInterval(heartbeatInterval);
          }
        } catch (error) {
          console.error('Heartbeat error:', error);
        }
      }, 30000); // Every 30 seconds

      return () => clearInterval(heartbeatInterval);
    }
  }, [user]);

  if (!trialInfo || !trialInfo.trialActive) {
    // User has subscription or no trial
    return null;
  }

  const percentage = (trialInfo.remainingMinutes / trialInfo.allowedMinutes) * 100;
  const minutes = Math.floor(trialInfo.remainingMinutes);
  const seconds = Math.floor((trialInfo.remainingMinutes - minutes) * 60);

  const getColor = () => {
    if (percentage > 50) return 'success';
    if (percentage > 25) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ mb: 2 }}>
      {showAlert && trialInfo.remainingMinutes <= 15 && trialInfo.remainingMinutes > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your trial is running low! Upgrade now to continue using all features.
        </Alert>
      )}

      {trialInfo.expired && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              variant="outlined"
              onClick={() => navigate('/upgrade')}
            >
              Upgrade Now
            </Button>
          }
        >
          Your trial has expired. Please upgrade to continue using the platform.
        </Alert>
      )}

      {!trialInfo.expired && (
        <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              Free Trial
            </Typography>
            <Typography variant="body2" color={getColor()}>
              {minutes}:{seconds.toString().padStart(2, '0')} remaining
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={percentage} 
            color={getColor()}
            sx={{ height: 8, borderRadius: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              {trialInfo.usedMinutes.toFixed(1)} of {trialInfo.allowedMinutes} minutes used
            </Typography>
            <Button 
              size="small" 
              variant="outlined" 
              color="primary"
              onClick={() => navigate('/upgrade')}
            >
              Upgrade for $10/mo
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TrialIndicator;
